import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { VoiceToTextTextarea } from "@/components/voice-field";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Search, LayoutGrid, List, Star, ChevronRight, Loader2, Plus, CheckCircle,
  Eye, Send, Building2, ExternalLink, Users, ArrowRight, PhoneCall,
  Trophy,
} from "lucide-react";

// ─── Types ─────────────────────────────────────────────────────────────────────

type ApprovalRequest = {
  id: string; funnelId: string; name: string; email: string; phone?: string | null;
  company?: string | null; businessType?: string | null; heardAbout?: string | null;
  teamSize?: string | null; annualRevenue?: string | null; annualShows?: number | null;
  goals?: string | null; currentTech: string[]; extraFields: Record<string, any>;
  stage: string; betaPhase?: string | null; adminScore?: number | null;
  adminNotes?: string | null; workspaceId?: string | null; workspaceCreatedAt?: string | null;
  credentialsSentAt?: string | null; source: string; createdAt: string; updatedAt: string;
  notes?: ApprovalNote[]; history?: StageHistoryEntry[];
};
type ApprovalNote = { id: string; platformUserId?: string | null; body: string; createdAt: string };
type StageHistoryEntry = { id: string; fromStage?: string | null; toStage: string; note?: string | null; createdAt: string };

// ─── Constants ─────────────────────────────────────────────────────────────────

const STAGES = [
  { key: "submitted",      label: "Submitted", icon: ArrowRight, badgeCls: "bg-blue-100 text-blue-700",    boardCls: "border-blue-200 bg-blue-50/60" },
  { key: "reviewed",       label: "Pending",   icon: Eye,        badgeCls: "bg-amber-100 text-amber-700",  boardCls: "border-amber-200 bg-amber-50/60" },
  { key: "call_scheduled", label: "Phase 1",   icon: PhoneCall,  badgeCls: "bg-purple-100 text-purple-700",boardCls: "border-purple-200 bg-purple-50/60" },
  { key: "phase_2",        label: "Phase 2",   icon: Trophy,     badgeCls: "bg-indigo-100 text-indigo-700",boardCls: "border-indigo-200 bg-indigo-50/60" },
  { key: "phase_3",        label: "Phase 3",   icon: Star,       badgeCls: "bg-teal-100 text-teal-700",    boardCls: "border-teal-200 bg-teal-50/60" },
  { key: "waitlist",       label: "Waitlist",  icon: Users,      badgeCls: "bg-slate-100 text-slate-600",  boardCls: "border-slate-200 bg-slate-50/60" },
];
const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.key, s]));

const BETA_PHASES = [
  { value: "phase_1", label: "Phase 1" },
  { value: "phase_2", label: "Phase 2" },
  { value: "phase_3", label: "Phase 3" },
  { value: "phase_4", label: "Phase 4" },
];

const TECH_LABELS: Record<string, string> = {
  mass_emailer: "Mass Email", crm: "CRM", invoicing: "Invoicing", lead_gen: "Lead Gen",
  quickbooks: "QuickBooks", event_manager: "Event Manager", contract_signing: "E-Signature",
  scheduling: "Scheduling", social_media: "Social Media", payment_processing: "Payments",
  spreadsheets: "Spreadsheets", nothing: "Pen & Paper",
};

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function PlatformBetaApprovalsPage() {
  const [view, setView] = useState<"table" | "kanban">("table");
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [openRequestId, setOpenRequestId] = useState<string | null>(null);

  const { data: requests = [], isLoading } = useQuery<ApprovalRequest[]>({
    queryKey: ["/api/platform/beta/requests"],
  });
  const { data: counts = {} } = useQuery<Record<string, number>>({
    queryKey: ["/api/platform/beta/requests/counts"],
    queryFn: () => fetch("/api/platform/beta/requests/counts", { credentials: "include" }).then((r) => r.json()),
  });

  const filtered = requests.filter((r) => {
    if (stageFilter !== "all" && r.stage !== stageFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || (r.company ?? "").toLowerCase().includes(q);
  });

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <PlatformLayout>
      <div className="space-y-5" data-testid="page-platform-beta-approvals">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold">Beta Approvals</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {total} application{total !== 1 ? "s" : ""} total · Public form at{" "}
              <a href="/beta" target="_blank" rel="noopener noreferrer" className="text-primary inline-flex items-center gap-0.5 hover:underline text-sm">
                /beta <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open("/beta", "_blank")} data-testid="button-preview-beta-form">
              <Eye className="w-4 h-4 mr-1.5" /> Preview Form
            </Button>
            <Button variant={view === "table" ? "default" : "outline"} size="sm" onClick={() => setView("table")} data-testid="button-view-table">
              <List className="w-4 h-4 mr-1.5" /> Table
            </Button>
            <Button variant={view === "kanban" ? "default" : "outline"} size="sm" onClick={() => setView("kanban")} data-testid="button-view-kanban">
              <LayoutGrid className="w-4 h-4 mr-1.5" /> Kanban
            </Button>
          </div>
        </div>

        {/* Stage filter pills */}
        <div className="flex items-center gap-2 flex-wrap">
          {[{ key: "all", label: `All (${total})` }, ...STAGES.map((s) => ({ key: s.key, label: `${s.label} (${counts[s.key] ?? 0})` }))].map((f) => (
            <button
              key={f.key}
              onClick={() => setStageFilter(f.key)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                stageFilter === f.key
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-400 dark:bg-transparent dark:text-gray-300 dark:border-gray-700",
              )}
              data-testid={`filter-stage-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search name, email, company…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
            data-testid="input-search-approvals"
          />
        </div>

        {/* View */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : view === "table" ? (
          <TableView requests={filtered} onOpen={setOpenRequestId} />
        ) : (
          <KanbanView requests={filtered} onOpen={setOpenRequestId} />
        )}
      </div>

      <RequestDetailDialog
        requestId={openRequestId}
        open={!!openRequestId}
        onOpenChange={(v) => { if (!v) setOpenRequestId(null); }}
      />
    </PlatformLayout>
  );
}

// ─── Table View ────────────────────────────────────────────────────────────────

function TableView({ requests, onOpen }: { requests: ApprovalRequest[]; onOpen: (id: string) => void }) {
  if (requests.length === 0) {
    return (
      <Card className="p-12 text-center text-muted-foreground">
        <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p className="font-medium">No applications yet</p>
        <p className="text-sm mt-1">
          Share the <a href="/beta" target="_blank" className="text-primary hover:underline">/beta</a> link to start collecting applications.
        </p>
      </Card>
    );
  }
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Applicant</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Business</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Shows / yr</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Score</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Stage</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Phase</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Applied</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {requests.map((r) => {
              const stage = STAGE_MAP[r.stage];
              return (
                <tr key={r.id} className="hover:bg-muted/20 cursor-pointer" onClick={() => onOpen(r.id)} data-testid={`row-applicant-${r.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p>{r.company ?? "—"}</p>
                    <p className="text-xs text-muted-foreground">{r.businessType ?? "—"}</p>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{r.annualShows ?? "—"}</td>
                  <td className="px-4 py-3">
                    <StarRating value={r.adminScore ?? 0} readonly size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    {stage && (
                      <Badge className={cn("text-xs", stage.badgeCls)}>{stage.label}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {r.betaPhase ? (
                      <Badge variant="outline" className="text-xs">
                        {BETA_PHASES.find((p) => p.value === r.betaPhase)?.label ?? r.betaPhase}
                      </Badge>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpen(r.id)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Kanban View ───────────────────────────────────────────────────────────────

function KanbanView({ requests, onOpen }: { requests: ApprovalRequest[]; onOpen: (id: string) => void }) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {STAGES.map((stage) => {
        const cols = requests.filter((r) => r.stage === stage.key);
        return (
          <div key={stage.key} className="flex-shrink-0 w-72">
            <div className={cn("rounded-xl border p-3 h-full", stage.boardCls)}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <stage.icon className="w-4 h-4 text-current opacity-70" />
                  <span className="text-sm font-medium">{stage.label}</span>
                </div>
                <Badge variant="outline" className="text-xs">{cols.length}</Badge>
              </div>
              <div className="space-y-2">
                {cols.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6 opacity-60">Empty</p>
                ) : (
                  cols.map((r) => (
                    <div
                      key={r.id}
                      className="bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-border p-3 cursor-pointer hover:shadow-sm transition-all"
                      onClick={() => onOpen(r.id)}
                      data-testid={`card-applicant-${r.id}`}
                    >
                      <div className="flex items-start justify-between gap-1 mb-1">
                        <p className="font-medium text-sm leading-tight truncate">{r.name}</p>
                        {(r.adminScore ?? 0) > 0 && <StarRating value={r.adminScore!} readonly size="xs" />}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{r.company || r.businessType || r.email}</p>
                      {r.betaPhase && (
                        <Badge variant="outline" className="text-xs mt-1.5">
                          {BETA_PHASES.find((p) => p.value === r.betaPhase)?.label ?? r.betaPhase}
                        </Badge>
                      )}
                      <p className="text-xs text-muted-foreground mt-1.5">{new Date(r.createdAt).toLocaleDateString()}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Detail Dialog ─────────────────────────────────────────────────────────────

function RequestDetailDialog({
  requestId, open, onOpenChange,
}: {
  requestId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [noteBody, setNoteBody] = useState("");
  const [score, setScore] = useState(0);
  const [adminNotes, setAdminNotes] = useState("");
  const [betaPhase, setBetaPhase] = useState("");

  const { data: request, isLoading } = useQuery<ApprovalRequest & { notes: ApprovalNote[]; history: StageHistoryEntry[] }>({
    queryKey: ["/api/platform/beta/requests", requestId],
    queryFn: () => fetch(`/api/platform/beta/requests/${requestId}`, { credentials: "include" }).then((r) => r.json()),
    enabled: !!requestId && open,
  });

  useEffect(() => {
    if (request) {
      setScore(request.adminScore ?? 0);
      setAdminNotes(request.adminNotes ?? "");
      setBetaPhase(request.betaPhase ?? "");
    }
  }, [request?.id]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["/api/platform/beta/requests"] });
    qc.invalidateQueries({ queryKey: ["/api/platform/beta/requests", requestId] });
    qc.invalidateQueries({ queryKey: ["/api/platform/beta/requests/counts"] });
  };

  const stageMutation = useMutation({
    mutationFn: ({ stage, phase }: { stage: string; phase?: string }) =>
      apiRequest("POST", `/api/platform/beta/requests/${requestId}/stage`, { stage, betaPhase: phase || null }),
    onSuccess: () => { invalidate(); toast({ title: "Stage updated" }); },
    onError: () => toast({ title: "Failed to update stage", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: object) => apiRequest("PATCH", `/api/platform/beta/requests/${requestId}`, data),
    onSuccess: () => { invalidate(); toast({ title: "Saved" }); },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const noteMutation = useMutation({
    mutationFn: (body: string) => apiRequest("POST", `/api/platform/beta/requests/${requestId}/notes`, { body }),
    onSuccess: () => { invalidate(); setNoteBody(""); toast({ title: "Note added" }); },
    onError: () => toast({ title: "Failed to add note", variant: "destructive" }),
  });

  const credsMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/platform/beta/requests/${requestId}/send-credentials`, {}),
    onSuccess: () => { invalidate(); toast({ title: "Credentials marked as sent" }); },
    onError: () => toast({ title: "Failed", variant: "destructive" }),
  });

  if (!open || !requestId) return null;

  const stageDef = request ? STAGE_MAP[request.stage] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {isLoading || !request ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap pr-6">
                <div>
                  <DialogTitle className="text-xl">{request.name}</DialogTitle>
                  <DialogDescription className="mt-0.5">
                    {request.email}
                    {request.phone ? ` · ${request.phone}` : ""}
                    {request.company ? ` · ${request.company}` : ""}
                  </DialogDescription>
                </div>
                {stageDef && (
                  <Badge className={cn("text-sm px-3 py-1", stageDef.badgeCls)}>{stageDef.label}</Badge>
                )}
              </div>
            </DialogHeader>

            <div className="space-y-6 pt-2">

              {/* ── Pipeline actions ── */}
              <Card>
                <CardContent className="pt-4 pb-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Move to stage</p>
                  <div className="flex flex-wrap gap-2">
                    {STAGES.filter((s) => s.key !== request.stage).map((s) => (
                      <Button
                        key={s.key}
                        variant="outline"
                        size="sm"
                        onClick={() => stageMutation.mutate({ stage: s.key, phase: s.key === "approved" ? betaPhase : undefined })}
                        disabled={stageMutation.isPending}
                        className={cn(
                          "text-xs",
                          s.key === "approved" && "border-green-300 text-green-700 hover:bg-green-50",
                          s.key === "rejected" && "border-red-300 text-red-600 hover:bg-red-50",
                        )}
                        data-testid={`button-stage-${s.key}`}
                      >
                        <s.icon className="w-3.5 h-3.5 mr-1.5" />
                        {s.label}
                      </Button>
                    ))}
                  </div>

                  {/* Beta phase picker */}
                  <div className="flex items-center gap-3">
                    <Label className="text-xs shrink-0 text-muted-foreground">Beta Phase</Label>
                    <Select
                      value={betaPhase}
                      onValueChange={(v) => {
                        setBetaPhase(v);
                        updateMutation.mutate({ betaPhase: v });
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs w-36" data-testid="select-beta-phase">
                        <SelectValue placeholder="Assign phase" />
                      </SelectTrigger>
                      <SelectContent>
                        {BETA_PHASES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Workspace status */}
                  {request.workspaceId && (
                    <div className="flex items-center gap-2 text-xs bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      <Building2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                      <span className="text-green-800">
                        Workspace provisioned{request.workspaceCreatedAt ? ` on ${new Date(request.workspaceCreatedAt).toLocaleDateString()}` : ""}
                      </span>
                      {!request.credentialsSentAt ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="ml-auto h-6 text-xs border-green-300"
                          onClick={() => credsMutation.mutate()}
                          disabled={credsMutation.isPending}
                          data-testid="button-send-credentials"
                        >
                          <Send className="w-3 h-3 mr-1" /> Mark Credentials Sent
                        </Button>
                      ) : (
                        <span className="ml-auto text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Credentials sent
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ── Details + Admin review ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Application details */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Application Details</h3>
                  <dl className="space-y-2 text-sm">
                    {([
                      ["Business Type", request.businessType],
                      ["Team Size", request.teamSize],
                      ["Annual Revenue", request.annualRevenue],
                      ["Bookings / Year", request.annualShows?.toString()],
                      ["Heard About", request.heardAbout],
                    ] as [string, string | null | undefined][]).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <dt className="text-muted-foreground w-32 shrink-0">{k}</dt>
                        <dd className="font-medium">{v}</dd>
                      </div>
                    ))}
                  </dl>

                  {Array.isArray(request.currentTech) && request.currentTech.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1.5">Current Tech Stack</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(request.currentTech as string[]).map((k) => (
                          <Badge key={k} variant="outline" className="text-xs">{TECH_LABELS[k] ?? k}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {request.goals && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Goals</p>
                      <p className="text-sm bg-muted rounded-md px-3 py-2 leading-relaxed">{request.goals}</p>
                    </div>
                  )}
                </div>

                {/* Admin review */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Admin Review</h3>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Score (1–5)</Label>
                    <StarRating
                      value={score}
                      onChange={(v) => {
                        setScore(v);
                        updateMutation.mutate({ adminScore: v });
                      }}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Internal Notes</Label>
                    <VoiceToTextTextarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Private notes visible only to platform admins…"
                      rows={4}
                      className="text-sm"
                      data-testid="textarea-admin-notes"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateMutation.mutate({ adminNotes })}
                      disabled={updateMutation.isPending}
                      data-testid="button-save-notes"
                    >
                      {updateMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                      Save Notes
                    </Button>
                  </div>
                </div>
              </div>

              {/* ── Stage history ── */}
              {request.history && request.history.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3">Stage History</h3>
                  <div className="space-y-1.5">
                    {request.history.map((h) => {
                      const from = h.fromStage ? STAGE_MAP[h.fromStage]?.label ?? h.fromStage : null;
                      const to = STAGE_MAP[h.toStage]?.label ?? h.toStage;
                      return (
                        <div key={h.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="w-28 shrink-0">{new Date(h.createdAt).toLocaleDateString()}</span>
                          <ChevronRight className="w-3 h-3 shrink-0" />
                          <span>
                            {from && <><span className="font-medium">{from}</span> → </>}
                            <span className="font-medium">{to}</span>
                          </span>
                          {h.note && <span>· {h.note}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Notes thread ── */}
              <div>
                <h3 className="text-sm font-semibold mb-3">
                  Notes ({request.notes?.length ?? 0})
                </h3>
                <div className="space-y-2 mb-3">
                  {request.notes?.map((n) => (
                    <div key={n.id} className="bg-muted rounded-lg px-3 py-2.5">
                      <p className="text-xs text-muted-foreground mb-1">{new Date(n.createdAt).toLocaleString()}</p>
                      <p className="text-sm">{n.body}</p>
                    </div>
                  ))}
                  {(!request.notes || request.notes.length === 0) && (
                    <p className="text-sm text-muted-foreground">No notes yet.</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <VoiceToTextTextarea
                    placeholder="Add a note…"
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    rows={2}
                    className="text-sm"
                    data-testid="textarea-note"
                  />
                  <Button
                    onClick={() => noteMutation.mutate(noteBody)}
                    disabled={!noteBody.trim() || noteMutation.isPending}
                    className="shrink-0"
                    data-testid="button-add-note"
                  >
                    {noteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Star Rating ───────────────────────────────────────────────────────────────

function StarRating({
  value, onChange, readonly = false, size = "md",
}: {
  value: number; onChange?: (v: number) => void; readonly?: boolean; size?: "xs" | "sm" | "md";
}) {
  const [hover, setHover] = useState(0);
  const cls = size === "xs" ? "w-3 h-3" : size === "sm" ? "w-3.5 h-3.5" : "w-5 h-5";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(n)}
          onMouseEnter={() => !readonly && setHover(n)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={cn(readonly ? "cursor-default" : "cursor-pointer")}
          data-testid={!readonly ? `star-${n}` : undefined}
        >
          <Star className={cn(cls, "transition-colors", (hover || value) >= n ? "fill-amber-400 text-amber-400" : "text-gray-300")} />
        </button>
      ))}
    </div>
  );
}
